import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useStore } from '../../helpers/use-store';
import { useNotification } from '../../hooks/use-notification';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface DeleteVideoModalActions {
  open: (videoId: string) => void;
}

interface Props {
  onSuccess?: (videoId: string) => void;
}

const DeleteVideoModal = forwardRef<DeleteVideoModalActions, Props>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [videoId, setVideoId] = useState<string | null>(null);
    const { loadingState, updateLoadingState } = useLoading();
    const {
      dataStore: { videoStore },
    } = useStore();
    const notification = useNotification();

    useImperativeHandle(ref, () => ({
      open: (id: string) => {
        setVideoId(id);
        setIsOpen(true);
      },
    }));

    const handleDelete = async () => {
      if (!videoId) return;

      try {
        updateLoadingState(LoadingState.Loading);
        await videoStore.delete(videoId);

        notification.success({
          message: t('common.success'),
          description: t('videos.notifications.deleteSuccess'),
        });

        onSuccess?.(videoId);
        handleCancel();
      } catch (error) {
        console.error('Failed to delete video:', error);
        notification.error({
          message: t('common.error'),
          description: t('videos.notifications.deleteError'),
        });
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    const handleCancel = () => {
      setIsOpen(false);
      setVideoId(null);
    };

    return (
      <Modal
        title={t('videos.deleteModal.title')}
        open={isOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={loadingState === LoadingState.Loading}
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>,
        ]}
      >
        <Text>{t('videos.deleteModal.description')}</Text>
      </Modal>
    );
  }
);

export default DeleteVideoModal;
